import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, Radio, Tooltip } from '@mui/material';
import CommonTable from '../../Shared/Tables/TableV1.tsx';
import { OrganisationFormData } from '../../../interfaces/Management.interface';
import { useNavigate } from 'react-router-dom';
import AccountDetailsModal from './AccountDetailsModal.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';

interface Account {
  account_id: string;
  name: string;
  comments: string;
  // other fields...
}
interface AccountsSectionProps {
  accounts: any[]; // replace with appropriate interface if available
  organisation: OrganisationFormData | null;
  onAccountCreated: () => void;
  onAccountSelect: (accountId: string) => void;
}

const AccountsSection: React.FC<AccountsSectionProps> = ({ accounts, organisation, onAccountCreated, onAccountSelect, }) => {
  const navigate = useNavigate();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [proposals, setProposals] = useState<any[]>([]);
  const [detailAccount, setDetailAccount] = useState<Account | null>(null); // account for modal
  const [modalOpen, setModalOpen] = useState(false);
  const { showSnackBar } = useSnackBar();

  const handleRadioChange = (accountId: string) => {
    setSelectedAccountId(accountId);
    onAccountSelect(accountId);
  };

  const handleDetailsClick = (account: Account) => {
    setDetailAccount(account);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setDetailAccount(null);
  };

  useEffect(() => {
    setSelectedAccountId(null);
  }, [organisation?.organisation_id]);

  const accountColumns = useMemo(() => [
    {
      accessorKey: 'select',
      header: 'Select',
      Cell: ({ row }: any) => (
        <Radio
           size="small"
          checked={selectedAccountId === row.original.account_id}
          onChange={() => handleRadioChange(row.original.account_id)}
          value={row.original.account_id}
          name="account-radio"
          inputProps={{ 'aria-label': row.original.name }}
        />
      ),
      enableSorting: false,
      size: 50,
    },
    {
      accessorKey: 'name',
      header: 'Account Name',
      enableSorting: false,
      Cell: ({ cell }: any) => cell.getValue(),
    },
    {
      accessorKey: 'comments',
      header: 'Comments',
      Cell: ({ cell }: any) => cell.getValue(),
      enableSorting: false,
    },
    {
      accessorKey: 'actions',
      header: 'Details',
      enableSorting: false,
      Cell: ({ row }: any) => (
        <Button
          variant="text"
          color="primary"
          size='small'
          onClick={() => handleDetailsClick(row.original)}
        >
          Details
        </Button>
      ),
    },
  ], [selectedAccountId]);

  const handleNewAccountClick = () => {
    if (!organisation?.organisation_id) {
      showSnackBar("Please select an organisation first.", "error");

      return;
    }
    navigate(`/r/management/createAccount?orgId=${organisation.organisation_id}&orgName=${encodeURIComponent(organisation.name || '')}`);
  };

  const handleEditAccount = (account: Account) => {
    navigate(`/r/management/editAccount`, {
      state: {
        account: {
          ...account,
          organisation: {
            organisation_id: account.organisation,
            name: organisation?.name || "",
          },
           contacts: account.contacts || [],
        },
      },
    });
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6" color="dark">Accounts</Typography>
        <Tooltip title="Add New Account">
        <Button
          variant="contained"
          color="primary"
          size='small'
          onClick={handleNewAccountClick}
          disabled={!organisation?.organisation_id}
        >
          New Account
        </Button>
        </Tooltip>
      </Box>
      <CommonTable
        columns={accountColumns}
        data={accounts}
        enablePagination={false}
      />
      <AccountDetailsModal
        title={detailAccount?.name || ''}
        open={modalOpen}
        account={detailAccount}
        onClose={handleCloseModal}
        onEdit={handleEditAccount}
      />
    </>
  );
};

export default AccountsSection;
